/**
 * Confirm setting a plan to Staffed — product decision 2026-10-06.
 *
 * Staffed is a commit: it writes the plan's confirmed Resources onto the Solution as
 * Assignments, which moves the Solution's Fit Score and every one of those Resources'
 * load. So it is never a one-click select. This dialog shows exactly what will be written
 * — who, in which Position, with how many points, and whom they replace — read fresh from
 * the server each time it opens, and the same read refuses with the reason when the plan
 * is not ready.
 */

import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { usePlanStaffingPreview, useSetPlanResourcingStatus } from "@/hooks/use-resource-plans";
import type { PlannedAssignment } from "@/lib/plan-staffing";
import {
  disciplinesFromPlannedPod,
  type PositionDisciplines,
  positionLabel,
} from "@/lib/position-label";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

interface PlanStaffingConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: ResourcePlanDetailView;
}

function formatHours(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function changeNote(a: PlannedAssignment, disciplines: PositionDisciplines): string {
  if (a.keepsSeat && a.currentRole !== null) {
    return `Already ${positionLabel(a.currentRole, disciplines)} — unchanged`;
  }
  const notes: string[] = [];
  if (a.currentRole !== null) {
    notes.push(
      a.role === "Supporting"
        ? `leaves ${positionLabel(a.currentRole, disciplines)}`
        : `moves from ${positionLabel(a.currentRole, disciplines)}`,
    );
  }
  if (a.supportingPointsCleared !== null) {
    notes.push(`Supporting ${a.supportingPointsCleared} pts → 0`);
  }
  if (a.replacesName !== null) notes.push(`replaces ${a.replacesName}`);
  if (a.cappedAtCapacity) notes.push("capped at capacity");
  return notes.join(" · ");
}

export function PlanStaffingConfirmDialog({
  open,
  onOpenChange,
  plan,
}: PlanStaffingConfirmDialogProps) {
  const preview = usePlanStaffingPreview(plan.id, open);
  const setStatus = useSetPlanResourcingStatus(plan.id);
  const staffing = preview.data;
  // "R1 · SA": what each Position is for, from the plan's own Planned Pod.
  const disciplines = disciplinesFromPlannedPod(plan.plannedPod?.positions);
  // RP-32.1: a Resource the client asked for is flagged where the RM commits the staffing.
  const clientRequested = new Set(
    plan.lines.flatMap((l) =>
      l.suggestedResourceId !== null && l.suggestedResourceReason === "ClientRequested"
        ? [l.suggestedResourceId]
        : [],
    ),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Set “{plan.name}” to Staffed</DialogTitle>
          <DialogDescription>
            The plan’s confirmed Resources go onto the{" "}
            {plan.solutionContext?.solutionName ?? "Solution"}’s Pod. Points are sized from each
            Resource’s average monthly hours on the plan — except on a split line, where each
            Resource gets the points entered for them.
          </DialogDescription>
        </DialogHeader>

        {preview.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : preview.isError ? (
          <ErrorState
            message={
              preview.error instanceof Error
                ? preview.error.message
                : "Could not work out the staffing."
            }
            onRetry={() => preview.refetch()}
          />
        ) : staffing === undefined ? null : !staffing.ready ? (
          <div className="space-y-2 rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-sm text-warning-800 dark:border-warning-900/40 dark:bg-warning-950/30 dark:text-warning-300">
            <p className="font-medium">This plan can’t be set to Staffed yet.</p>
            <ul className="list-disc space-y-1 pl-5">
              {staffing.unconfirmedLineCount > 0 && (
                <li>
                  {staffing.unconfirmedLineCount} Plan Line
                  {staffing.unconfirmedLineCount === 1 ? " is" : "s are"} not Confirmed.
                </li>
              )}
              {staffing.blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Resource</th>
                  <th className="px-3 py-2 font-medium">Position</th>
                  <th className="px-3 py-2 text-right font-medium">Avg hrs / mo</th>
                  <th className="px-3 py-2 text-right font-medium">Points</th>
                  <th className="px-3 py-2 font-medium">Change</th>
                </tr>
              </thead>
              <tbody>
                {staffing.assignments.map((a) => (
                  <tr key={a.resourceId} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">
                      {a.resourceName}
                      {clientRequested.has(a.resourceId) && (
                        <span
                          className="ml-1.5 rounded bg-warning-100 px-1 text-xs font-semibold text-warning-950"
                          title="Client Requested on this plan"
                        >
                          Client Requested
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">{positionLabel(a.role, disciplines)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatHours(a.avgMonthlyHours)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {a.keepsSeat ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <>
                          {a.currentPoints !== null && a.currentPoints !== a.points && (
                            <span className="text-muted-foreground">{a.currentPoints} → </span>
                          )}
                          <span className="font-semibold">{a.points}</span>
                          {/*
                           * RP-24: say where the figure came from. Line Share points are the
                           * RM's own; every other line's are sized from its hours.
                           */}
                          <span
                            className="block text-xs font-normal text-muted-foreground"
                            title={
                              a.pointsSource === "hours"
                                ? "Sized from the plan's hours"
                                : a.pointsSource === "rmEntered"
                                  ? "The points the RM entered on this Resource's Line Shares"
                                  : a.pointsSource === "seatPlusShares"
                                    ? "Their current points in the seat they hold, plus the points entered on their Line Shares"
                                    : "Sized from their unsplit lines' hours, plus the points entered on their Line Shares"
                            }
                          >
                            {a.pointsSource === "hours"
                              ? "from hours"
                              : a.pointsSource === "rmEntered"
                                ? "RM-entered"
                                : a.pointsSource === "seatPlusShares"
                                  ? "seat + RM-entered"
                                  : "hours + RM-entered"}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {changeNote(a, disciplines)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={setStatus.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setStatus.mutate("Staffed", { onSuccess: () => onOpenChange(false) })}
            disabled={staffing?.ready !== true || setStatus.isPending}
          >
            {setStatus.isPending ? "Staffing…" : "Set to Staffed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
